import Marker from '../Marker';
import subpixel from '../util/canvasSubpixelOffset';

/** Bar marker */
class BarMarker extends Marker {

	getDefaultParams() {
		return {
			fillColor: '#000'
		};
	}

	/**
	 * Renders data to a canvas using the provided scales and styling parameters
	 *
	 * @param {object} panel - Panel information
	 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
	 * @param {Array} data - Data to be rendered
	 * @param {function} xScale - Function to scale x-values
	 * @param {function} yScale - Function to scale y-values
	 * @param {string} fillColor - Color for filling the bars
	 * @param {string} align - Alignment of the bars, default is 'default'
	 * @param {string|null} radii - Radii for rounded corners, null for square corners
	 */
	render(panel, ctx, data, xScale, yScale, fillColor, align = 'default', radii = null) {
	// Calculate the width of the bars and the y-axis range
		const widthBar = this.getBarWidth(panel);
		const [yMin, yMax] = yScale.range();
		const yZero = yScale(0); // Y-coordinate for zero value

		// Initialize last x and y coordinates
		let [xLast, yLast] = data[data.length - 1].map(d => Math.round(yScale(d)));

		// Parse the radii for rounded corners, if provided
		radii = this._parseRadii(radii);

		// Determine the top y-coordinate for drawing bars
		let yTop;
		if (yZero > yMax) {
			yTop = yMax;
		} else if (yZero < yMin) {
			yTop = yMin;
		} else {
			yTop = yZero;
		}

		// Set up canvas drawing styles
		ctx.lineWidth = 1;
		ctx.fillStyle = fillColor || this.params.fillColor;
		ctx.strokeStyle = ctx.fillStyle;
		ctx.beginPath();

		// Iterate through the data to draw each bar
		for (const [xValue, yValue] of data) {
			if (yValue === 0) continue; // Ignore data points with no value

			const x = xScale(xValue);
			let y = yScale(yValue);
			const heightBar = yTop - y; // Calculate the height of the bar

			// Adjust y-coordinate based on alignment
			if (align === 'bottom') {
				y = yMax - heightBar - 2;
			}

			// Draw the bars only if there is a change in coordinates
			if (x !== xLast || y < yLast) {
				if (widthBar > 0) {
					// Draw rounded or square bars based on radii
					if (radii) {
						subpixel.roundRect(ctx, x - widthBar, y, widthBar * 2, heightBar, radii);
					} else {
						subpixel.fillRect(ctx, x - widthBar, y, widthBar * 2, heightBar);
					}
				} else {
					// Draw a line for very narrow bars
					subpixel.moveTo(ctx, x, y);
					subpixel.lineTo(ctx, x, y + heightBar);
				}

				// Update the last drawn coordinates
				xLast = x;
				yLast = y;
			}
		}

		// Finalize drawing with fill or stroke based on bar width
		if (widthBar > 0) {
			ctx.fill();
		} else {
			ctx.stroke();
		}
	}

	/**
	 * Parses the radii input for different formats.
	 *
	 * This method takes a radii input, which can be either a null, a string representing a single number,
	 * or a string representing an array of numbers. It then tries to parse the string and convert it
	 * into a single number or an array of numbers as required by the roundRect() method.
	 *
	 * @param {string|null} radii - The radii input to be parsed. Can be a string or null.
	 * @returns {number|number[]|null} - The parsed radii as a single number, an array of numbers, or null if parsing fails.
	 */
	_parseRadii(radii) {
	// Check if radii is null or empty
		if (radii === null || !radii.length) {
			return null;
		}

		// Try to parse as a single number
		const parsedNumber = parseFloat(radii);
		if (!isNaN(parsedNumber)) {
			return parsedNumber;
		}

		// Try to parse as an array
		try {
			const parsedArray = JSON.parse(radii);
			if (Array.isArray(parsedArray)) {
				return parsedArray.map(num => parseFloat(num));
			}
		} catch (e) {
			console.error('Invalid radii format');
		}

		// Return null if parsing fails
		return null;
	}
}

export default BarMarker;
